import React, { createContext, useContext, useEffect, useRef, useState, useMemo } from 'react';
import API from '../../Services/API';
import { useToasts } from '../Toasts';
import {getCookie, isLocal, useVisibilityChange, classNames } from '../../Services/helpers';
import experimentData from '../../Data/experiments';
import { captureException } from '../../Services/Sentry';
import DB from '../../Data/DB';
import { useOffline } from '../Offline';
import { usePreference } from '../../Data/Preference';

const SessionContext = createContext();

export const useSessionContext = () => {
    return useContext(SessionContext);
};

export const Provider = ({ children }) => {
    const {isOnline} = useOffline();
    const [isReady, setIsReady] = useState(false);
    const [hasToken, setHasToken] = useState(!!API.getToken());
    const [user, setUser] = useState(null);
    const [business, setBusiness] = useState(null);
    const [plan, setPlan] = useState(null);
    const [balance, setBalance] = useState(null);
    const [journal, setJournal] = useState(null);
    const [notifications, setNotifications] = useState(null);
    const [notificationBadge, setNotificationBadge] = useState(null);
    const {showError} = useToasts();
    const [deviceToken, setDeviceToken] = useState(null);
    const [notificationsFetchError, setNotificationsFetchError] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const fetchingNotifications = useRef(false);
    const [showRedesign, setShowRedesign] = usePreference('experimental:showRedesign', false);
    const [showDarkMode, setShowDarkMode] = usePreference('experimental:showDarkMode', false);

    useEffect(() => {
        window.toggleRedesign = () => {
            setShowRedesign(!showRedesign);
        }
        window.toggleDarkMode = () => {
            setShowDarkMode(!showDarkMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRedesign, showDarkMode]);

    useEffect(() => {
        if (showRedesign === false && showDarkMode === true) {
            setShowDarkMode(false);
            return;
        }

        if (showDarkMode) {
            document.body.classList.add('dark');
        } else {
            document.body.classList.remove('dark');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRedesign, showDarkMode]);

    useEffect(() => {
        API.onLogout.listen(logout);

        if (!DB.ready) {
            return;
        }

        const keys = ['balance', 'plan', 'user', 'business', 'journal', 'notifications'];
        DB.getMany(keys).then(data => {
            setBalance(data.balance);
            setPlan(data.plan);
            setUser(data.user);
            setBusiness(data.business);
            setJournal(data.journal);
            setNotifications(data.notifications);
            setIsReady(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDetails = async () => {
        try {
            const details = await API.get('/app/start');
            setUser(details.user);
            setBusiness(details.business);
            setPlan(details.plan);
            setBalance(details.balance);
            setJournal(details.journal);
            setIsReady(true);

            if (DB.ready) {
                DB.set('balance', details.balance);
                DB.set('plan', details.plan);
                DB.set('user', details.user);
                DB.set('business', details.business);
                DB.set('journal', details.journal);
            }
        } catch (e) {
            console.log(e);
            showError("Failed to fetch session details", e.message);
            captureException(e);
        }
    }

    const fetchNotifications = async () => {
        if (!API.getToken()) {
            return;
        }
        if (isOnline === false) {
            return;
        }
        if (fetchingNotifications?.current) {
            return;
        }
        fetchingNotifications.current = true;

        try {
            const _notifications = await API.get('/notifications/recent');
            setNotificationsFetchError(false);
            setNotifications(_notifications);
            setNotificationBadge(_notifications.filter(n => n.status === 'Active').length);
            fetchingNotifications.current = false;
        } catch (e) {
            captureException(e);
            setNotificationsFetchError(true);
            fetchingNotifications.current = false;
        }
    }

    const markAllNotificationsSeen = () => {
        return API.post('/notifications/markAllSeen')
            .catch(err => {
                showError(API.parseErrorResponse(err));
                captureException(err);
            })
            .then(fetchNotifications);
    }

    const markMatchingNotificationsSeen = (filter) => {
        if (!notifications || !notifications.length) {
            return;
        }

        const matches = notifications.filter(n => n.status === 'Active').filter(filter);

        if (!matches || !matches.length) {
            return;
        }

        const promises = matches.map(n => API.post(`/notifications/${n.notificationId}/see`));

        Promise.all(promises)
            .catch(err => {
                showError(API.parseErrorResponse(err));
                captureException(err);
            })
            .then(fetchNotifications);
    }

    const hasFeature = (feature) => {
        if (!plan) {
            return false;
        }

        const disabled = [
            // 'ADDITIONAL_USERS',
            // 'WEEK_PLANNER',
            // 'BANK_RECONCILIATION',
            // 'AI_ASSISTANT',
            // 'SAVED_REPLIES',
            // 'ROUTE_OPTIMISATION',
            // 'CUSTOMER_PORTAL',
            // 'ADVANCED_SCHEDULING',
            // 'EXPENSES',
            // 'LOYALTY_CARDS',
            // 'DOCUMENTS',
            // 'TAGS'
            // 'SAVED_SEARCHES',
            // 'ATTACHMENTS',
            // 'CUSTOMER_NOTIFICATIONS',
        ];

        if (isLocal() && disabled.includes(feature)) {
            console.log('Feature disabled in dev mode', feature);
            return false;
        }

        return plan?.features[feature]?.enabled ?? false;
    }

    const isRoleSufficient = (role) => {
        return API.isRoleSufficient(role);
    }

    const login = (data) => {
        return API.post('/auth/login', data)
            .then((res) => {
                API.setToken(res.token);
                setUser(res.user);
                setBusiness(res.business);
            })
            .catch(err => {
                showError(API.parseErrorResponse(err));
                captureException(err);
            })
    }

    const logout = () => {
        console.log('Resetting session');
        setIsReady(false);
        setBalance(null);
        setPlan(null);
        setUser(null);
        setBusiness(null);
        setJournal(null);
        setNotifications(null);
        API.reset();
        DB.reset();

        return API.post('/auth/logout', {deviceToken})
            .then(() => {
                //
            })
            .catch((err) => {
                showError(API.parseErrorResponse(err));
                captureException(err);
            }).finally(() => {
                API.reset();
            });
    }

    useEffect(() => {
        setHasToken(!!API.getToken());

        if (API.getToken()) {
            const sid = API.jwt('sid');
            if (sid !== sessionId) {
                setSessionId(sid);
            }

            fetchDetails();
            fetchNotifications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [API.getToken()]);

    useVisibilityChange(() => {
        fetchNotifications();
    });

    const selectExperimentGroup = (visitorId, groups) => {
        if (!visitorId) {
            return groups[0] ?? null;
        }
        const string = visitorId.replace(/[^0-9a-f]/gi, '');
        const intVal = parseInt(string.slice(-3), 16);
        const groupCount = groups.length;
        const group = intVal % groupCount;

        return groups[group];
    }

    const experiments = useMemo(() => {
        const visitorId = getCookie('visitor-id');

        return Object.keys(experimentData).reduce((carry, name) => {
            carry[name] = selectExperimentGroup(visitorId, experimentData[name]);
            return carry;
        }, {});
    }, []);

    const adminSwitchInto = async (businessId) => {
        await DB.clear();
        return API.switchIntoBusiness(businessId);
    }

    const adminSwitchBack = async () => {
        await DB.clear();
        return API.switchBack();
    }

    const providerValues = {
        user,
        business,
        plan,
        balance,
        journal,
        notifications,
        notificationBadge,
        setNotificationBadge,
        notificationsFetchError,
        markAllNotificationsSeen,
        markMatchingNotificationsSeen,
        fetchDetails,
        fetchNotifications,
        hasFeature,
        refreshSession: fetchDetails,
        refresh: fetchDetails,
        login,
        logout,
        hasToken,
        isReady,
        isRoleSufficient,
        isAdmin: (level) => API.isAdmin(level),
        isAdminSwitched: API.isAdminSwitchedBusiness(),
        adminSwitchInto,
        adminSwitchBack,
        adminLevel: user?.isAdmin ?? null,
        setDeviceToken,
        sessionId,
        experiments,
        experiment: (name) => experiments[name],
        showRedesign,
        setShowRedesign,
        showDarkMode,
        setShowDarkMode,
    };

    return (
        <SessionContext.Provider value={providerValues}>
            {children}
            <RedesignToggle {...{showRedesign, setShowRedesign, local: isLocal(), isAdmin: API.isAdmin('Super')}} />
        </SessionContext.Provider>
    );
};

const RedesignToggle = ({showRedesign, setShowRedesign, local, isAdmin}) => {
    if (!local || !isAdmin) {
        return null;
    }

    const toggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowRedesign(!showRedesign);
    }

    return <button className={classNames(
        'fixed top-0 right-4 z-99',
        'm-0 h-3 w-3',
        showRedesign && 'bg-green-600',
        !showRedesign && 'bg-red-400',
        )} onClick={toggle}>
    </button>
}
